import type { DeskAction, Family, FamilyGroup, OwnerParticipant, OwnerWaiting } from "@teamna/shared";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  Button,
  Callout,
  CloseIcon,
  EmptyState,
  ErrorState,
  InlineError,
  LockIcon,
  PageHeader,
  SkeletonList,
  Tabs,
  showToast,
  panelId,
  tabId,
} from "../../components/index.ts";
import { useActiveBusiness } from "@teamna/web-client/hooks/use-owner.ts";
import { useNewParticipants, useRemoveNewParticipant } from "@teamna/web-client/hooks/use-coach-participants.ts";
import { useFamilies } from "@teamna/web-client/hooks/use-families.ts";
import { useParticipants } from "@teamna/web-client/hooks/use-setup.ts";
import { useFamilyActions, useFamilyWords } from "./actions.ts";
import { AddFamiliesPanel } from "./add.tsx";
import { GroupsBoard } from "./board.tsx";
import { capital, isPlaced, type Words } from "./model.ts";
import { ParticipantsList, type ParticipantDelete } from "./participants.tsx";
import type { FamilyActions } from "./types.ts";

export type ParticipantsView = "list" | "board";

/**
 * The participants page (owner menu revamp, 2026-10-09): "Swimmers" at a swimming academy. Every
 * child in one list with a search, and the groups board where the owner puts each one in a group.
 * Both were tabs of the Families page. Each view has its own address (`/owner/participants`,
 * `/owner/participants/board`), so the Parents page can open the board directly.
 */
export function ParticipantsPageView({
  families,
  participants,
  groups,
  words,
  actions,
  view,
  onView,
  waiting,
  onRetryWaiting,
  onOpenParent,
  onOpenWaiting,
  onDeleteWaiting,
  onOpenCsv,
  may = () => true,
}: {
  families: Family[];
  /** Each child's package and sessions left. Missing while it loads: the rows show no count. */
  participants?: OwnerParticipant[];
  groups: FamilyGroup[];
  words: Words;
  actions: FamilyActions;
  view: ParticipantsView;
  onView: (view: ParticipantsView) => void;
  /** The names coaches added that wait for a parent: in the list, not on the board. */
  waiting?: OwnerWaiting[];
  /** Set when the waiting names did not load: the page says some names are missing, with Try again. */
  onRetryWaiting?: () => void;
  onOpenParent?: (familyId: string) => void;
  onOpenWaiting?: () => void;
  /** "Not our swimmer" for a waiting name. */
  onDeleteWaiting?: (id: string) => Promise<void>;
  onOpenCsv: () => void;
  /**
   * The jobs this person's role may do (`useActiveBusiness().may`): the front desk (T1) does not
   * delete or place. Missing: every job, as for the owner (Storybook).
   */
  may?: (action: DeskAction) => boolean;
}) {
  const [error, setError] = useState<unknown>(null);
  // A child is "Remove child", with Undo in the toast; a waiting name has no Undo.
  const onDelete = async (target: ParticipantDelete) => {
    try {
      if (target.kind === "child") {
        const { family, child } = target;
        await actions.removeChild(family.id, child.participantId);
        showToast(`${child.name} deleted.`, {
          action: { label: "Undo", onClick: () => void actions.restoreChild(family.id, child.participantId).catch(setError) },
        });
      } else if (onDeleteWaiting) {
        await onDeleteWaiting(target.row.id);
        showToast(`${target.row.name} deleted.`);
      }
    } catch (failure) {
      setError(failure);
    }
  };
  const [add, setAdd] = useState<{ open: boolean; group: string; key: number }>({ open: false, group: "none", key: 0 });
  const children = families.flatMap((family) => family.children);
  const noGroup = children.filter((child) => !isPlaced(child, groups)).length;

  const tabs = [
    { id: "list", label: "List", count: children.length + (waiting?.length ?? 0) },
    ...(may("placeParticipant") ? [{ id: "board", label: `By ${words.group}`, count: noGroup }] : []),
  ];

  return (
    <div className="max-w-[1200px]">
      <PageHeader
        title={capital(words.participants)}
        description={
          noGroup > 0
            ? `${noGroup} ${noGroup === 1 ? `${words.participant} has` : `${words.participants} have`} no ${words.group} yet. Their ${words.members} see no sessions for them.`
            : `Every ${words.participant}, their ${words.group} and their ${words.members}.`
        }
      />
      {error ? (
        <div className="relative mb-4">
          <InlineError error={error} />
          <button type="button" onClick={() => setError(null)} aria-label="Hide this message" className="absolute top-3 right-3 text-ink-2">
            <CloseIcon size={16} />
          </button>
        </div>
      ) : null}
      {onRetryWaiting ? (
        <Callout tone="late" title={`The ${words.participants} with no ${words.member} yet did not load`}>
          <p>
            The list shows only the {words.participants} who have a {words.member}.
          </p>
          <Button size="sm" variant="ghost" onClick={onRetryWaiting}>
            Try again
          </Button>
        </Callout>
      ) : null}
      <Tabs tabs={tabs} selectedId={view} onSelect={(id) => onView(id as ParticipantsView)} label="Views" />
      <div role="tabpanel" id={panelId(view)} aria-labelledby={tabId(view)} className="pt-5">
        {view === "list" ? (
          <ParticipantsList
            families={families}
            groups={groups}
            words={words}
            waiting={waiting}
            participants={participants}
            onOpenParent={onOpenParent}
            onOpenWaiting={onOpenWaiting}
            {...(may("removeParticipant") ? { onDelete } : {})}
          />
        ) : groups.length === 0 ? (
          <EmptyState
            icon={<LockIcon size={28} />}
            title={`No ${words.groups} yet`}
            message={`Make your ${words.groups} on the "${capital(words.groups)}" page. Then put each ${words.participant} in one here.`}
          />
        ) : (
          <GroupsBoard
            families={families}
            groups={groups}
            words={words}
            actions={actions}
            onAddList={(group) => setAdd((current) => ({ open: true, group, key: current.key + 1 }))}
            onError={setError}
          />
        )}
      </div>
      <AddFamiliesPanel
        key={add.key}
        open={add.open}
        onClose={() => setAdd((current) => ({ ...current, open: false }))}
        groups={groups}
        words={words}
        actions={actions}
        initialGroup={add.group}
        onOpenCsv={onOpenCsv}
        onError={setError}
      />
    </div>
  );
}

/** The route, one per view. */
export function OwnerParticipantsScreen({ view = "list" }: { view?: ParticipantsView }) {
  const { business, isLoading, error } = useActiveBusiness();
  if (isLoading && !business) return <SkeletonList />;
  if (error && !business) return <ErrorState error={error} />;
  if (!business) {
    return <EmptyState icon={<LockIcon size={30} />} title="No business to manage" message="This account does not run a business yet." />;
  }
  return <ParticipantsBody businessId={business.businessId} view={view} />;
}

function ParticipantsBody({ businessId, view }: { businessId: string; view: ParticipantsView }) {
  const navigate = useNavigate();
  const { may } = useActiveBusiness();
  const words = useFamilyWords();
  const families = useFamilies(businessId);
  // Sessions left of each child (#ai-feedback 2026-10-30), the read of the Parents list.
  const participants = useParticipants(businessId);
  const actions = useFamilyActions(businessId);
  // A failed read leaves the waiting names out: the linked swimmers still show, with a note that
  // some names are missing (`onRetryWaiting`), so the list never looks complete when it is not.
  const newOnes = useNewParticipants(may("linkNewParticipant") ? businessId : null);
  const removeWaiting = useRemoveNewParticipant(businessId);
  // A coach can say a swimmer who has a parent is theirs too: that swimmer is in the list already.
  const waiting = (newOnes.data?.waiting ?? []).filter((row) => row.participant === null);

  if (families.isLoading) return <SkeletonList />;
  if (families.error || !families.data) return <ErrorState error={families.error} onRetry={() => void families.refetch()} />;

  return (
    <ParticipantsPageView
      families={families.data.families}
      participants={participants.data}
      groups={families.data.groups}
      words={words}
      actions={actions}
      view={view}
      onView={(next) => void navigate({ to: next === "board" ? "/owner/participants/board" : "/owner/participants" })}
      waiting={waiting}
      onRetryWaiting={newOnes.error ? () => void newOnes.refetch() : undefined}
      onOpenParent={(id) => void navigate({ to: "/owner/parents/$id", params: { id } })}
      {...(may("linkNewParticipant")
        ? { onOpenWaiting: () => void navigate({ to: "/owner/new-participants" }), onDeleteWaiting: (id: string) => removeWaiting.mutateAsync(id) }
        : {})}
      onOpenCsv={() => void navigate({ to: "/owner/setup/import/csv" })}
      may={may}
    />
  );
}
